import { v4 as uuid } from 'uuid'

import { getAuthFromCache } from './useAuth'

export const getHeaders = () => {
    const token = getAuthFromCache()?.data?.token
    const baseHeaders = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'correlation-id': uuid(),
        'Accept-Language': 'sv-SE',
        Authorization: `Bearer ${token}`,
    }
    return { ...baseHeaders }
}

export const getHeadersFileUpload = () => {
    const token = getAuthFromCache()?.data?.token
    const baseHeaders = {
        Accept: 'application/json',
        'correlation-id': uuid(),
        'Accept-Language': 'sv-SE',
        Authorization: `Bearer ${token}`,
    }
    return { ...baseHeaders }
}

export const getHeadersExportCsv = (columns: any[]) => {
    const token = getAuthFromCache()?.data?.token
    const baseHeaders = {
        'Content-Type': 'text/csv',
        'x-column-headers': encodeURI(JSON.stringify(columns)),
        'correlation-id': uuid(),
        'Accept-Language': 'sv-SE',
        Authorization: `Bearer ${token}`,
    }
    return { ...baseHeaders }
}

const parseJson = async (response: Response) => {
    try {
        return await response.json()
    } catch (_error) {
        return ''
    }
}

const handleErrorResponse = async (response: Response) => {
    let error
    try {
        const data = await response.json()
        error = data
    } catch (_e) {
        error = response
    }
    if (error.status === undefined) {
        error.status = response.status
    }
    return Promise.reject(error)
}

export const fetchGet = async (endpoint: string, headers: any = getHeaders()) => {
    return fetch(endpoint, {
        method: 'GET',
        headers: headers,
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return await parseJson(response)
    })
}

export const fetchGetBlob = async (endpoint: string, headers: any = getHeaders()) => {
    return fetch(endpoint, {
        method: 'GET',
        headers: headers,
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return response
    })
}

export const fetchGetDownload = async (
    endpoint: string,
    filename?: string,
    headers: any = getHeaders(),
) => {
    return fetch(endpoint, {
        method: 'GET',
        headers: headers,
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return resolveBlob(response, filename)
    })
}

export const fetchGetThumbnail = async (endpoint: string, headers: any = getHeaders()) => {
    return fetch(endpoint, {
        method: 'GET',
        headers: headers,
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return resolveUrl(response)
    })
}

export const fetchPost = async (
    endpoint: string,
    payload: string | FormData | undefined,
    headers: any = getHeaders(),
) => {
    return fetch(endpoint, {
        method: 'POST',
        body: payload,
        headers: headers,
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return await parseJson(response)
    })
}

export const fetchPostFile = async (
    endpoint: string,
    payload: any,
    headers: any = getHeadersFileUpload(),
) => {
    return fetch(endpoint, {
        method: 'POST',
        body: payload,
        headers: headers,
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return await parseJson(response)
    })
}

export const fetchPutFile = async (
    endpoint: string,
    payload: any,
    headers: any = getHeadersFileUpload(),
) => {
    return fetch(endpoint, {
        method: 'PUT',
        body: payload,
        headers: headers,
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return await parseJson(response)
    })
}

export const fetchPatch = async (endpoint: string, payload: string) => {
    return fetch(endpoint, {
        method: 'PATCH',
        body: payload,
        headers: getHeaders(),
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return await parseJson(response)
    })
}

export const fetchPut = async (endpoint: string, payload: string, headers = getHeaders()) => {
    return fetch(endpoint, {
        method: 'PUT',
        body: payload,
        headers: headers,
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return await parseJson(response)
    })
}

export const fetchDelete = async (endpoint: string) => {
    return fetch(endpoint, {
        method: 'DELETE',
        headers: getHeaders(),
    }).then(async (response) => {
        if (!response.ok) {
            return handleErrorResponse(response)
        }
        return await parseJson(response)
    })
}

const resolveBlob = async (response: Response, fallbackFileName?: string) => {
    const fileName = fallbackFileName ?? getFileNameFromContentDisposition(response)
    if (!fileName) {
        throw new Error('Could not resolve asset filename for download')
    }
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    link.remove()
}

const getFileNameFromContentDisposition = (response: Response): string | undefined => {
    const contentDisposition = response.headers.get('content-disposition')
    const filenamePart = contentDisposition?.split(';')[1]?.split('=')[1]
    if (filenamePart) {
        return decodeURI(filenamePart.replace(/"/g, ''))
    }

    return undefined
}

const resolveUrl = async (response: Response) => {
    const url = window.URL.createObjectURL(await response.blob())
    return url
}
